import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import EsimAPI from 'api/faber/EsimAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import {format, isBefore} from 'date-fns'
import { formatExpiryText } from 'helpers/date'
import OrbitSession from 'hooks/useCookies'

import TerminateVoiceDialog from '../dialogs/TerminateVoicePlanDialog'

import {
  CellText,
  fitTableHeight,
  listGridProps,
  ListSection,
  MutedText,
  RowActionsMenu,
  StatusPill,
  UsageBar
} from './listUi'

interface VoicePlan {
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string | null;
  createdBy: string;
  updatedBy: string;
  UserID: string;
  InitialOutgoing: number;
  Outgoing: number;
  InitialIncoming: number;
  Incoming: number;
  PaymentIntent: string;
  ExpiresAt: string;
  ID: string;
  CatalogID: string;
  catalog: {
    name: string;
    voiceInMins: number;
    validityInDays: number;
  }
}

const columns = (
  onTerminate: (row: VoicePlan) => void,
) => [
  {
    field: 'catalogName',
    headerName: 'Pack',
    sortable: false,
    flex: 1.4,
    minWidth: 200,
    renderCell: (params: { row: VoicePlan }) => (
      <CellText strong>{params.row.catalog?.name}</CellText>
    )
  },
  {
    field: 'usage',
    headerName: 'Voice Remaining',
    sortable: false,
    flex: 1.2,
    minWidth: 170,
    renderCell: (params: { row: VoicePlan }) => {
      const {Outgoing, InitialOutgoing} = params.row;
      const unUsedInPercentage = Outgoing / InitialOutgoing * 100;
      const unUsedInMins = (Outgoing / 60).toFixed(2);
      const totalMins = (InitialOutgoing / 60).toFixed(2);
      return (
        <UsageBar
          value={unUsedInPercentage}
          label={`${unUsedInMins} / ${totalMins} mins`}
        />
      )
    }
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: false,
    flex: 0.7,
    minWidth: 110,
    renderCell: (params: { row: VoicePlan }) => {
      if (params.row.DeletedAt !== null) {
        return <StatusPill status='TERMINATED' />
      }
      const isExpired = isBefore(new Date(params.row.ExpiresAt), new Date());
      return <StatusPill status={isExpired ? 'EXPIRED' : 'ACTIVE'} />
    }
  },
  {
    field: 'validity',
    headerName: 'Validity',
    sortable: false,
    flex: 0.6,
    minWidth: 90,
    renderCell: (params: { row: VoicePlan }) => (
      <CellText>{`${params.row?.catalog?.validityInDays} days`}</CellText>
    )
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    sortable: false,
    flex: 0.9,
    minWidth: 140,
    renderCell: (params: { row: VoicePlan }) => (
      <MutedText>
        {format(new Date(params.row.CreatedAt), 'dd MMM yyyy, HH:mm')}
      </MutedText>
    )
  },
  {
    field: 'expiresAt',
    headerName: 'Expires',
    sortable: false,
    flex: 0.9,
    minWidth: 140,
    renderCell: (params: { row: VoicePlan }) => (
      <MutedText>{formatExpiryText(new Date(params.row.ExpiresAt))}</MutedText>
    )
  },
  {
    field: 'actions',
    headerName: '',
    sortable: false,
    flex: 0,
    width: 56,
    align: 'right' as const,
    renderCell: (params: { row: VoicePlan }) =>
      params.row.DeletedAt !== null ? null : (
        <RowActionsMenu
          actions={[
            {
              label: 'Terminate voice pack',
              danger: true,
              onClick: () => onTerminate(params.row)
            }
          ]}
        />
      )
  }
]

function VoicePacksCard({ userId }: { userId: string }) {
  const faberToken = OrbitSession.get('faberToken')
  const [openTerminateDialog, setOpenTerminateDialog] = useState<boolean>(false);
  const [selectedPack, setSelectedPack] = useState<VoicePlan | null>(null);

  const { data: voicePlans, isLoading } = useQuery(
    ['customer-voice-plans', userId],
    async () => {
      const response = await EsimAPI.getCustomerVoicePlans({
        userId,
        faberToken,
      });
      return response.data?.data;
    }
  );

  const onTerminate = (row: VoicePlan) => {
    setOpenTerminateDialog(true);
    setSelectedPack(row);
  }

  return (
    <>
      <ListSection title='Voice Packs' count={voicePlans?.length ?? 0}>
        <MUIDataGrid
          {...listGridProps}
          columns={columns(onTerminate)}
          rows={voicePlans || []}
          getRowId={(row: VoicePlan) => row.ID}
          loading={isLoading}
          hideFooter
          tableHeight={fitTableHeight(voicePlans?.length ?? 0)}
        />
      </ListSection>

      {selectedPack && (
        <TerminateVoiceDialog
          planId={selectedPack.ID}
          faberToken={faberToken}
          onClose={() => setOpenTerminateDialog(false)}
          open={openTerminateDialog}
        />
      )}
    </>
  )
}


export default VoicePacksCard
